import { API_ENDPOINTS, API_ERROR_MESSAGES } from "../constants/api";
import { apiFetch } from "./apiClient";

export function validateWelcomeLetterLan(value) {
  const loanAccountNumber = String(value ?? "").trim();

  if (!loanAccountNumber) {
    throw new Error("Loan account number is required.");
  }

  if (!/^\d{1,20}$/.test(loanAccountNumber)) {
    throw new Error(
      "Loan account number must contain only digits and be at most 20 digits long.",
    );
  }

  return loanAccountNumber;
}

async function readErrorMessage(response, fallbackMessage) {
  try {
    const result = await response.json();
    return result?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

function formatLoanAmount(value) {
  const amount = String(value ?? "").trim();

  if (!amount || amount === "-") {
    return "-";
  }

  if (/^(?:\u20B9|INR\b)/i.test(amount)) {
    return amount;
  }

  return `\u20B9 ${amount}`;
}

function coordinatorDetails(data) {
  if (data.group_coordinator_details) {
    return data.group_coordinator_details;
  }

  const name = String(data.group_coordinator_name ?? "").trim();
  const phone = String(data.group_coordinator_phone ?? "").trim();

  if (name && phone) {
    return `${name} (${phone})`;
  }

  return name || phone || "-";
}

export function mapWelcomeLetterPreview(data = {}) {
  return {
    loanAccountNumber: String(data.loan_account_number ?? ""),
    customerName: data.customer_name || "-",
    loanProduct: data.loan_product || "-",
    groupName: data.group_name || "-",
    groupCoordinatorDetails: coordinatorDetails(data),
    loanAmount: formatLoanAmount(data.approved_amount),
    mobileNumber: data.mobile_number || "-",
    emiAmount: data.emi_amount || "-",
    firstEmiDate: data.first_emi_date || "-",
    lastEmiDate: data.maturity_date || "-",
    downloadAvailable: data.download_available === true,
  };
}

export async function fetchWelcomeLetterPreview(value) {
  const loanAccountNumber = validateWelcomeLetterLan(value);
  const response = await apiFetch(
    API_ENDPOINTS.welcomeLetter.preview(loanAccountNumber),
  );

  let result;

  try {
    result = await response.json();
  } catch {
    throw new Error(API_ERROR_MESSAGES.welcomeLetterPreviewReadFailed);
  }

  if (!response.ok || result?.status !== "OK" || !result?.data) {
    const fallbackMessage =
      response.status === 404
        ? "Welcome Letter details were not found for this loan account number."
        : API_ERROR_MESSAGES.welcomeLetterPreviewFetchFailed;

    throw new Error(result?.message || fallbackMessage);
  }

  return mapWelcomeLetterPreview(result.data);
}

function responseFilename(response, fallbackFilename) {
  const contentDisposition =
    response.headers.get("Content-Disposition") || "";
  const filenameMatch = contentDisposition.match(
    /filename\*?=(?:UTF-8''|")?([^";]+)/i,
  );

  if (!filenameMatch) {
    return fallbackFilename;
  }

  try {
    return decodeURIComponent(filenameMatch[1].replace(/"/g, "").trim());
  } catch {
    return filenameMatch[1].replace(/"/g, "").trim() || fallbackFilename;
  }
}

export async function downloadWelcomeLetterPdf(value) {
  const loanAccountNumber = validateWelcomeLetterLan(value);
  const response = await apiFetch(
    API_ENDPOINTS.welcomeLetter.pdf(loanAccountNumber),
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        API_ERROR_MESSAGES.welcomeLetterPdfDownloadFailed,
      ),
    );
  }

  const contentType = response.headers.get("Content-Type") || "";

  if (!contentType.toLowerCase().includes("application/pdf")) {
    throw new Error(API_ERROR_MESSAGES.welcomeLetterInvalidPdf);
  }

  const blob = await response.blob();
  const filename = responseFilename(
    response,
    `Welcome_Letter_${loanAccountNumber}.pdf`,
  );
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  try {
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  return filename;
}
